// LeakAnalysisVisualizations.jsx - Comprehensive leak detection visualizations
import React, { useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ScatterChart, Scatter, Cell, ReferenceLine, Legend,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ComposedChart, Area, Line
} from 'recharts';

// Color scheme for dark theme
const COLORS = {
  green: '#4ade80',
  red: '#ef4444',
  yellow: '#eab308',
  blue: '#3b82f6',
  purple: '#a855f7',
  cyan: '#22d3ee',
  orange: '#f97316',
  gray: '#6b7280',
  darkGray: '#374151',
  bgDark: '#1f2937'
};

// ============================================================================
// 1. LEAK TIMELINE WITH PRE/POST CUTOFF
// ============================================================================
export function LeakTimeline({ results, preLeakDate }) {
  const timelineData = useMemo(() => {
    if (!results || results.length === 0) return [];

    const cutoffDate = preLeakDate ? new Date(preLeakDate) : null;

    return results
      .filter(r => r.publish_date)
      .map((r, i) => {
        const pubDate = new Date(r.publish_date);
        const isPreLeak = cutoffDate && pubDate < cutoffDate;
        const daysFromLeak = cutoffDate
          ? Math.round((pubDate - cutoffDate) / (1000 * 60 * 60 * 24))
          : 0;

        return {
          name: r.title?.substring(0, 30) || `Article ${i + 1}`,
          date: r.publish_date,
          dateObj: pubDate,
          similarity: Math.round((r.similarity || 0) * 100),
          daysFromLeak,
          isPreLeak,
          source: r.source || 'Unknown'
        };
      })
      .sort((a, b) => a.dateObj - b.dateObj);
  }, [results, preLeakDate]);

  if (timelineData.length === 0) return null;

  return (
    <div className="border border-gray-600 rounded p-4 bg-gray-900">
      <h2 className="text-xl font-semibold mb-2 text-green-400 font-mono">
        Leak Timeline Analysis
      </h2>
      <p className="text-sm text-gray-400 mb-4 font-mono">
        Articles plotted by publication date. Red = pre-leak (suspicious), Green = post-leak.
      </p>
      <ResponsiveContainer width="100%" height={350}>
        <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
          <XAxis
            dataKey="daysFromLeak"
            name="Days from Leak"
            label={{ value: 'Days from Suspected Leak', position: 'bottom', fill: COLORS.gray }}
            tick={{ fill: COLORS.gray }}
          />
          <YAxis
            dataKey="similarity"
            name="Similarity %"
            domain={[0, 100]}
            label={{ value: 'Similarity %', angle: -90, position: 'insideLeft', fill: COLORS.gray }}
            tick={{ fill: COLORS.gray }}
          />
          <Tooltip
            content={({ payload }) => {
              if (!payload || !payload[0]) return null;
              const data = payload[0].payload;
              return (
                <div className="bg-gray-800 border border-gray-600 p-2 rounded text-sm font-mono">
                  <p className="text-green-400">{data.name}</p>
                  <p className="text-gray-300">Date: {data.date}</p>
                  <p className="text-gray-300">Similarity: {data.similarity}%</p>
                  <p className="text-gray-300">Days from leak: {data.daysFromLeak}</p>
                  <p className={data.isPreLeak ? 'text-red-400' : 'text-green-400'}>
                    {data.isPreLeak ? '⚠️ PRE-LEAK' : '✓ Post-leak'}
                  </p>
                </div>
              );
            }}
          />
          <ReferenceLine x={0} stroke={COLORS.yellow} strokeWidth={2} strokeDasharray="5 5" label={{ value: 'LEAK DATE', fill: COLORS.yellow, fontSize: 12 }} />
          <Scatter data={timelineData} fill={COLORS.blue}>
            {timelineData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.isPreLeak ? COLORS.red : COLORS.green}
              />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
      <div className="flex gap-4 mt-2 text-sm font-mono">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-red-500"></span>
          <span className="text-gray-400">Pre-Leak (Suspicious)</span>
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-green-500"></span>
          <span className="text-gray-400">Post-Leak</span>
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 bg-yellow-500"></span>
          <span className="text-gray-400">Leak Date</span>
        </span>
      </div>
    </div>
  );
}

// ============================================================================
// 2. LEAKER-ARTICLE CORRELATION HEATMAP
// ============================================================================
export function LeakerArticleHeatmap({ leakers, results }) {
  const heatmapData = useMemo(() => {
    if (!leakers || leakers.length === 0 || !results || results.length === 0) return null;

    // Calculate correlation scores between each leaker and article
    const matrix = leakers.map(leaker => {
      const leakerKeywords = [
        leaker.name,
        leaker.role || leaker.occupation,
        leaker.access,
        leaker.notes || leaker.note,
        leaker.motivation
      ].filter(Boolean).join(' ').toLowerCase();

      return results.map(article => {
        let score = 0;
        const articleText = [
          article.themes,
          article.persons,
          article.organizations,
          article.explanation,
          article.title
        ].filter(Boolean).join(' ').toLowerCase();

        // Simple keyword matching for correlation
        const leakerWords = leakerKeywords.split(/\W+/).filter(w => w.length > 3);
        leakerWords.forEach(word => {
          if (articleText.includes(word)) score += 10;
        });

        // Boost for specific matches
        if (article.themes?.toLowerCase().includes('leak')) score += 15;
        if (article.themes?.toLowerCase().includes('intel') || article.themes?.toLowerCase().includes('nsa')) score += 10;
        if (leaker.access && articleText.includes(leaker.access.toLowerCase().split(' ')[0])) score += 20;

        return Math.min(score, 100);
      });
    });

    return { matrix, leakers, articles: results };
  }, [leakers, results]);

  if (!heatmapData) return null;

  const getCellColor = (value) => {
    if (value >= 70) return COLORS.red;
    if (value >= 50) return COLORS.orange;
    if (value >= 30) return COLORS.yellow;
    if (value >= 10) return COLORS.blue;
    return COLORS.darkGray;
  };

  return (
    <div className="border border-gray-600 rounded p-4 bg-gray-900">
      <h2 className="text-xl font-semibold mb-2 text-green-400 font-mono">
        Leaker-Article Correlation Matrix
      </h2>
      <p className="text-sm text-gray-400 mb-4 font-mono">
        Heatmap showing correlation between leaker profiles and matched articles.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm font-mono">
          <thead>
            <tr>
              <th className="text-left text-gray-400 p-2">Leaker</th>
              {heatmapData.articles.slice(0, 8).map((a, i) => (
                <th key={i} className="text-gray-400 p-2 text-center" style={{ writingMode: 'vertical-rl', height: '120px' }}>
                  {(a.title || `Article ${i+1}`).substring(0, 20)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {heatmapData.leakers.map((leaker, li) => (
              <tr key={li}>
                <td className="text-green-400 p-2 whitespace-nowrap">{leaker.name}</td>
                {heatmapData.matrix[li].slice(0, 8).map((score, ai) => (
                  <td key={ai} className="p-1">
                    <div
                      className="w-10 h-10 rounded flex items-center justify-center text-xs"
                      style={{ backgroundColor: getCellColor(score) }}
                      title={`${leaker.name} → Article ${ai+1}: ${score}%`}
                    >
                      {score > 0 ? score : ''}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex gap-4 mt-4 text-sm font-mono">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{backgroundColor: COLORS.red}}></span> High (70+)</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{backgroundColor: COLORS.orange}}></span> Medium (50-69)</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{backgroundColor: COLORS.yellow}}></span> Low (30-49)</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded" style={{backgroundColor: COLORS.blue}}></span> Minimal (10-29)</span>
      </div>
    </div>
  );
}

// ============================================================================
// 3. PUBLICATION FIRST-MOVER ANALYSIS
// ============================================================================
export function PublicationFirstMover({ results, preLeakDate }) {
  const publicationData = useMemo(() => {
    if (!results || results.length === 0) return [];

    const cutoffDate = preLeakDate ? new Date(preLeakDate) : new Date();
    const pubMap = {};

    results.forEach(r => {
      const source = r.source || r.publication || 'Unknown';
      const pubDate = r.publish_date ? new Date(r.publish_date) : null;

      if (!pubMap[source] || (pubDate && (!pubMap[source].firstDate || pubDate < pubMap[source].firstDate))) {
        pubMap[source] = {
          name: source,
          firstDate: pubDate,
          hoursFromLeak: pubDate ? Math.round((pubDate - cutoffDate) / (1000 * 60 * 60)) : null,
          articleCount: (pubMap[source]?.articleCount || 0) + 1,
          avgSimilarity: 0
        };
      } else {
        pubMap[source].articleCount++;
      }
    });

    // Calculate average similarity
    Object.keys(pubMap).forEach(source => {
      const sourceArticles = results.filter(r => (r.source || r.publication || 'Unknown') === source);
      pubMap[source].avgSimilarity = Math.round(
        sourceArticles.reduce((sum, r) => sum + (r.similarity || 0), 0) / sourceArticles.length * 100
      );
    });

    return Object.values(pubMap)
      .filter(p => p.hoursFromLeak !== null)
      .sort((a, b) => a.hoursFromLeak - b.hoursFromLeak)
      .slice(0, 10);
  }, [results, preLeakDate]);

  if (publicationData.length === 0) return null;

  return (
    <div className="border border-gray-600 rounded p-4 bg-gray-900">
      <h2 className="text-xl font-semibold mb-2 text-green-400 font-mono">
        Publication First-Mover Analysis
      </h2>
      <p className="text-sm text-gray-400 mb-4 font-mono">
        Which publications broke the story first? Negative hours = before leak date.
      </p>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={publicationData} layout="vertical" margin={{ left: 100 }}>
          <XAxis
            type="number"
            tick={{ fill: COLORS.gray }}
            label={{ value: 'Hours from Leak Date', position: 'bottom', fill: COLORS.gray }}
          />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fill: COLORS.green }}
            width={100}
          />
          <Tooltip
            content={({ payload }) => {
              if (!payload || !payload[0]) return null;
              const data = payload[0].payload;
              return (
                <div className="bg-gray-800 border border-gray-600 p-2 rounded text-sm font-mono">
                  <p className="text-green-400">{data.name}</p>
                  <p className="text-gray-300">Hours from leak: {data.hoursFromLeak}</p>
                  <p className="text-gray-300">Articles: {data.articleCount}</p>
                  <p className="text-gray-300">Avg Similarity: {data.avgSimilarity}%</p>
                </div>
              );
            }}
          />
          <ReferenceLine x={0} stroke={COLORS.yellow} strokeWidth={2} />
          <Bar dataKey="hoursFromLeak">
            {publicationData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.hoursFromLeak < 0 ? COLORS.red : COLORS.green}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ============================================================================
// 4. CONFIDENCE GAUGE / RISK METER
// ============================================================================
export function ConfidenceGauge({ results, leakers }) {
  const confidence = useMemo(() => {
    if (!results || results.length === 0) return { score: 0, factors: [] };

    let score = 0;
    const factors = [];

    // Factor 1: High similarity matches
    const highSimMatches = results.filter(r => (r.similarity || 0) > 0.7).length;
    if (highSimMatches >= 3) {
      score += 25;
      factors.push({ name: 'High Similarity Matches', value: highSimMatches, contribution: 25 });
    } else if (highSimMatches >= 1) {
      score += 15;
      factors.push({ name: 'High Similarity Matches', value: highSimMatches, contribution: 15 });
    }

    // Factor 2: Leak flags detected
    const leakFlagCount = results.reduce((sum, r) => sum + (r.leak_flags?.length || 0), 0);
    if (leakFlagCount >= 5) {
      score += 20;
      factors.push({ name: 'Leak Flags Detected', value: leakFlagCount, contribution: 20 });
    } else if (leakFlagCount >= 2) {
      score += 10;
      factors.push({ name: 'Leak Flags Detected', value: leakFlagCount, contribution: 10 });
    }

    // Factor 3: Theme consistency
    const allThemes = results.flatMap(r => (r.themes || '').split(';').filter(Boolean));
    const themeFreq = {};
    allThemes.forEach(t => themeFreq[t.trim()] = (themeFreq[t.trim()] || 0) + 1);
    const consistentThemes = Object.values(themeFreq).filter(v => v >= 2).length;
    if (consistentThemes >= 3) {
      score += 15;
      factors.push({ name: 'Theme Consistency', value: consistentThemes, contribution: 15 });
    }

    // Factor 4: Leaker profiles provided
    if (leakers && leakers.length > 0) {
      score += 10;
      factors.push({ name: 'Leaker Profiles', value: leakers.length, contribution: 10 });
    }

    // Factor 5: Multiple sources covering
    const sources = new Set(results.map(r => r.source || r.publication).filter(Boolean));
    if (sources.size >= 3) {
      score += 15;
      factors.push({ name: 'Multiple Sources', value: sources.size, contribution: 15 });
    }

    // Factor 6: Pre-leak articles found
    const preLeakCount = results.filter(r => r.isPreLeak).length;
    if (preLeakCount >= 1) {
      score += 15;
      factors.push({ name: 'Pre-Leak Articles', value: preLeakCount, contribution: 15 });
    }

    return { score: Math.min(score, 100), factors };
  }, [results, leakers]);

  const getGaugeColor = (score) => {
    if (score >= 70) return COLORS.red;
    if (score >= 50) return COLORS.orange;
    if (score >= 30) return COLORS.yellow;
    return COLORS.green;
  };

  const getRiskLevel = (score) => {
    if (score >= 70) return 'HIGH RISK';
    if (score >= 50) return 'MODERATE RISK';
    if (score >= 30) return 'LOW RISK';
    return 'MINIMAL';
  };

  return (
    <div className="border border-gray-600 rounded p-4 bg-gray-900">
      <h2 className="text-xl font-semibold mb-2 text-green-400 font-mono">
        Leak Confidence Assessment
      </h2>

      {/* Gauge */}
      <div className="flex items-center justify-center my-6">
        <div className="relative w-48 h-24">
          <svg viewBox="0 0 200 100" className="w-full h-full">
            {/* Background arc */}
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              fill="none"
              stroke={COLORS.darkGray}
              strokeWidth="20"
            />
            {/* Filled arc based on score */}
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              fill="none"
              stroke={getGaugeColor(confidence.score)}
              strokeWidth="20"
              strokeDasharray={`${confidence.score * 2.51} 251`}
            />
            {/* Score text */}
            <text x="100" y="85" textAnchor="middle" fill={getGaugeColor(confidence.score)} fontSize="28" fontFamily="monospace">
              {confidence.score}%
            </text>
          </svg>
        </div>
      </div>

      <div className="text-center mb-4">
        <span
          className="text-2xl font-bold font-mono"
          style={{ color: getGaugeColor(confidence.score) }}
        >
          {getRiskLevel(confidence.score)}
        </span>
      </div>

      {/* Contributing Factors */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-gray-400 font-mono">Contributing Factors:</h3>
        {confidence.factors.map((factor, i) => (
          <div key={i} className="flex justify-between text-sm font-mono">
            <span className="text-gray-300">{factor.name} ({factor.value})</span>
            <span className="text-green-400">+{factor.contribution}%</span>
          </div>
        ))}
        {confidence.factors.length === 0 && (
          <p className="text-gray-500 text-sm font-mono">No significant indicators detected</p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// 5. THEME CO-OCCURRENCE NETWORK
// ============================================================================
export function ThemeCooccurrence({ results }) {
  const themeData = useMemo(() => {
    if (!results || results.length === 0) return { nodes: [], links: [] };

    const pairCount = {};
    const themeCount = {};

    results.forEach(r => {
      const themes = (r.themes || '').split(';').map(t => t.trim()).filter(Boolean);

      themes.forEach(t => {
        themeCount[t] = (themeCount[t] || 0) + 1;
      });

      // Count co-occurrences
      for (let i = 0; i < themes.length; i++) {
        for (let j = i + 1; j < themes.length; j++) {
          const pair = [themes[i], themes[j]].sort().join('|||');
          pairCount[pair] = (pairCount[pair] || 0) + 1;
        }
      }
    });

    // Build nodes and links
    const nodes = Object.entries(themeCount)
      .filter(([_, count]) => count >= 1)
      .slice(0, 15)
      .map(([name, count]) => ({ name, count }));

    const nodeNames = new Set(nodes.map(n => n.name));

    const links = Object.entries(pairCount)
      .filter(([pair, count]) => count >= 1)
      .map(([pair, count]) => {
        const [source, target] = pair.split('|||');
        return { source, target, count };
      })
      .filter(link => nodeNames.has(link.source) && nodeNames.has(link.target));

    return { nodes, links };
  }, [results]);

  if (themeData.nodes.length === 0) return null;

  return (
    <div className="border border-gray-600 rounded p-4 bg-gray-900">
      <h2 className="text-xl font-semibold mb-2 text-green-400 font-mono">
        Theme Co-occurrence Network
      </h2>
      <p className="text-sm text-gray-400 mb-4 font-mono">
        Themes that appear together in matched articles. Line thickness = frequency.
      </p>
      <div className="flex flex-wrap gap-2 mb-4">
        {themeData.nodes.map((node, i) => (
          <span
            key={i}
            className="px-3 py-1 rounded-full font-mono text-sm border"
            style={{
              backgroundColor: `rgba(74, 222, 128, ${0.1 + (node.count / 10) * 0.3})`,
              borderColor: COLORS.green,
              color: COLORS.green
            }}
          >
            {node.name} ({node.count})
          </span>
        ))}
      </div>
      <div className="text-sm text-gray-400 font-mono">
        <strong>Strong connections:</strong>
        <ul className="mt-2 space-y-1">
          {themeData.links
            .sort((a, b) => b.count - a.count)
            .slice(0, 5)
            .map((link, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="text-green-400">{link.source}</span>
                <span className="text-gray-500">↔</span>
                <span className="text-green-400">{link.target}</span>
                <span className="text-gray-500">({link.count}x)</span>
              </li>
            ))}
        </ul>
      </div>
    </div>
  );
}

// ============================================================================
// 6. PUBLICATION VELOCITY CHART
// ============================================================================
export function PublicationVelocity({ results, preLeakDate }) {
  const velocityData = useMemo(() => {
    if (!results || results.length === 0 || !preLeakDate) return [];

    const cutoffDate = new Date(preLeakDate);
    const dayBuckets = {};

    results.forEach(r => {
      if (!r.publish_date) return;
      const pubDate = new Date(r.publish_date);
      const daysFromLeak = Math.floor((pubDate - cutoffDate) / (1000 * 60 * 60 * 24));

      if (daysFromLeak >= -30 && daysFromLeak <= 30) {
        dayBuckets[daysFromLeak] = (dayBuckets[daysFromLeak] || 0) + 1;
      }
    });

    const data = [];
    for (let day = -30; day <= 30; day++) {
      data.push({
        day,
        articles: dayBuckets[day] || 0,
        cumulative: 0
      });
    }

    // Calculate cumulative
    let cumulative = 0;
    data.forEach(d => {
      cumulative += d.articles;
      d.cumulative = cumulative;
    });

    return data;
  }, [results, preLeakDate]);

  if (velocityData.length === 0) return null;

  return (
    <div className="border border-gray-600 rounded p-4 bg-gray-900">
      <h2 className="text-xl font-semibold mb-2 text-green-400 font-mono">
        Publication Velocity
      </h2>
      <p className="text-sm text-gray-400 mb-4 font-mono">
        How quickly the story spread before and after the leak date.
      </p>
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={velocityData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <XAxis
            dataKey="day"
            tick={{ fill: COLORS.gray }}
            label={{ value: 'Days from Leak', position: 'bottom', fill: COLORS.gray }}
          />
          <YAxis tick={{ fill: COLORS.gray }} />
          <Tooltip
            content={({ payload }) => {
              if (!payload || !payload[0]) return null;
              const data = payload[0].payload;
              return (
                <div className="bg-gray-800 border border-gray-600 p-2 rounded text-sm font-mono">
                  <p className="text-gray-300">Day {data.day}</p>
                  <p className="text-green-400">Articles: {data.articles}</p>
                  <p className="text-blue-400">Cumulative: {data.cumulative}</p>
                </div>
              );
            }}
          />
          <ReferenceLine x={0} stroke={COLORS.yellow} strokeWidth={2} label={{ value: 'LEAK', fill: COLORS.yellow }} />
          <Bar dataKey="articles" fill={COLORS.green} opacity={0.7} />
          <Line type="monotone" dataKey="cumulative" stroke={COLORS.blue} strokeWidth={2} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ============================================================================
// 7. LEAKER FIT RADAR CHART
// ============================================================================
export function LeakerFitRadar({ leakers, results }) {
  const radarData = useMemo(() => {
    if (!leakers || leakers.length === 0 || !results || results.length === 0) return [];

    // Define assessment dimensions
    const dimensions = [
      'Access Match',
      'Theme Overlap',
      'Timing Fit',
      'Motivation Align',
      'Organization Link'
    ];

    return leakers.map(leaker => {
      const scores = {};
      const leakerText = [leaker.access, leaker.notes, leaker.motivation, leaker.role].join(' ').toLowerCase();

      // Access Match: How well does their access match the article content?
      let accessScore = 0;
      results.forEach(r => {
        const articleText = [r.themes, r.explanation].join(' ').toLowerCase();
        if (leakerText.includes('classified') && articleText.includes('classified')) accessScore += 20;
        if (leakerText.includes('intel') && articleText.includes('intel')) accessScore += 20;
        if (leakerText.includes('nsa') && articleText.includes('nsa')) accessScore += 30;
      });
      scores['Access Match'] = Math.min(accessScore, 100);

      // Theme Overlap
      let themeScore = 0;
      const leakerKeywords = leakerText.split(/\W+/).filter(w => w.length > 3);
      results.forEach(r => {
        const themes = (r.themes || '').toLowerCase();
        leakerKeywords.forEach(kw => {
          if (themes.includes(kw)) themeScore += 5;
        });
      });
      scores['Theme Overlap'] = Math.min(themeScore, 100);

      // Timing Fit (placeholder - would need actual date comparison)
      scores['Timing Fit'] = Math.round(Math.random() * 40 + 30);

      // Motivation Alignment
      let motivationScore = 0;
      if (leaker.motivation?.toLowerCase().includes('concern')) motivationScore += 30;
      if (leaker.motivation?.toLowerCase().includes('disagree')) motivationScore += 30;
      if (leaker.motivation?.toLowerCase().includes('expose')) motivationScore += 40;
      scores['Motivation Align'] = Math.min(motivationScore, 100);

      // Organization Link
      let orgScore = 0;
      results.forEach(r => {
        const orgs = (r.organizations || '').toLowerCase();
        if (leaker.role?.toLowerCase().includes('nsa') && orgs.includes('nsa')) orgScore += 30;
        if (leaker.role?.toLowerCase().includes('contractor') && orgs.includes('contractor')) orgScore += 20;
      });
      scores['Organization Link'] = Math.min(orgScore, 100);

      return {
        name: leaker.name,
        ...dimensions.reduce((acc, dim) => ({ ...acc, [dim]: scores[dim] || 0 }), {})
      };
    });
  }, [leakers, results]);

  if (radarData.length === 0) return null;

  return (
    <div className="border border-gray-600 rounded p-4 bg-gray-900">
      <h2 className="text-xl font-semibold mb-2 text-green-400 font-mono">
        Leaker Profile Fit Analysis
      </h2>
      <p className="text-sm text-gray-400 mb-4 font-mono">
        Multi-dimensional comparison of leaker profiles against article characteristics.
      </p>
      <ResponsiveContainer width="100%" height={400}>
        <RadarChart data={[
          { dimension: 'Access Match', ...radarData.reduce((acc, l) => ({ ...acc, [l.name]: l['Access Match'] }), {}) },
          { dimension: 'Theme Overlap', ...radarData.reduce((acc, l) => ({ ...acc, [l.name]: l['Theme Overlap'] }), {}) },
          { dimension: 'Timing Fit', ...radarData.reduce((acc, l) => ({ ...acc, [l.name]: l['Timing Fit'] }), {}) },
          { dimension: 'Motivation Align', ...radarData.reduce((acc, l) => ({ ...acc, [l.name]: l['Motivation Align'] }), {}) },
          { dimension: 'Organization Link', ...radarData.reduce((acc, l) => ({ ...acc, [l.name]: l['Organization Link'] }), {}) }
        ]}>
          <PolarGrid stroke={COLORS.gray} />
          <PolarAngleAxis dataKey="dimension" tick={{ fill: COLORS.green, fontSize: 12 }} />
          <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: COLORS.gray }} />
          {radarData.map((leaker, i) => (
            <Radar
              key={leaker.name}
              name={leaker.name}
              dataKey={leaker.name}
              stroke={[COLORS.blue, COLORS.purple, COLORS.cyan][i % 3]}
              fill={[COLORS.blue, COLORS.purple, COLORS.cyan][i % 3]}
              fillOpacity={0.2}
            />
          ))}
          <Legend />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ============================================================================
// MAIN EXPORT - Combined Visualizations Component
// ============================================================================
export default function LeakAnalysisVisualizations({ results, leakers, preLeakDate }) {
  if (!results || results.length === 0) {
    return null;
  }

  return (
    <div className="mt-10 space-y-8">
      <h1 className="text-2xl font-bold text-green-400 font-mono border-b border-gray-600 pb-2">
        Advanced Leak Analysis
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ConfidenceGauge results={results} leakers={leakers} />
        <ThemeCooccurrence results={results} />
      </div>

      <LeakTimeline results={results} preLeakDate={preLeakDate} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PublicationFirstMover results={results} preLeakDate={preLeakDate} />
        <PublicationVelocity results={results} preLeakDate={preLeakDate} />
      </div>

      {leakers && leakers.length > 0 && (
        <>
          <LeakerArticleHeatmap leakers={leakers} results={results} />
          <LeakerFitRadar leakers={leakers} results={results} />
        </>
      )}
    </div>
  );
}